import { FilePicker } from "@capawesome/capacitor-file-picker";
import { TextFieldTypes } from "@ionic/core";
import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonGrid,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonRow,
  IonText,
  IonThumbnail,
} from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import ReactS3Client from "react-aws-s3-typescript";
import {
  makeGETRequest,
  makePATCHRequest,
  makePOSTRequest,
} from "../../request/rawRequest";
import { Input } from "../../types/input";
import {
  ACCESS_KEY,
  REGION,
  S3_BUCKET,
  SECRET_ACCESS_KEY,
} from "../../variables/s3Variables";
import BasicPage from "../BasicPage/BasicPage";
import "./Account.css";
import DeleteAccount from "./DeleteAccount";
import StatModal from "./Stats/StatsModal";

import React from "react";
import { isAndroid, isBrowser } from "react-device-detect";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { User } from "../../types/user";
import { uploadFileToIos } from "../../utils/uploadImageToIOS";
import {
  getCurrentTechnoOrDisconnect,
  jobsMap,
} from "../../variables/jobDictionnary";
import Notifications from "./Notifications";

const s3Config = {
  bucketName: S3_BUCKET,
  region: REGION,
  accessKeyId: ACCESS_KEY,
  secretAccessKey: SECRET_ACCESS_KEY,
};

interface Prop {}

const logout = async () => {
  await makePOSTRequest("/logout");
};

interface Badge {
  name: string;
  picture: string;
}

const getRank = (level: number, ranks: Badge[]): Badge => {
  const rank = level % 3;
  return ranks[rank] ?? { name: "--à venir--", picture: "" };
};

const valued = async () => {
  let temp: Input[] = [];
  await makeGETRequest("/me").then((response) => {
    const InputsDefault: Input[] = [
      { name: "Nom", value: response.last_name, type: "text", change: false },
      {
        name: "Prénom",
        value: response.first_name,
        type: "text",
        change: false,
      },
      {
        name: "Téléphone",
        value: response.phone_number,
        type: "tel",
        change: false,
      },
      { name: "Email", value: response.email, type: "email", change: false },
      { name: "_id", value: response._id, type: "text", change: false },
    ];
    temp = InputsDefault;
  });
  return temp;
};

const Account: React.FC<Prop> = () => {
  const polesTechologie =
    jobsMap.get(getCurrentTechnoOrDisconnect())?.polesTechnologie ?? [];
  const polesTutorials =
    jobsMap.get(getCurrentTechnoOrDisconnect())?.polesTutorials ?? [];
  const [response] = useState<Input[]>([]);
  const [inputs, setInputs] = useState<Input[]>(response);
  const [user, setUser] = useState<User>();
  const [file, setFile] = useState<File>();
  const ranks = jobsMap.get(getCurrentTechnoOrDisconnect())?.badges ?? [];
  const notifNumber = useRef<number>(0);

  const [time, setTime] = useState<{ name: string; time: number }[]>([]);

  const fetchInfos = async () => {
    await valued().then((response) => {
      setInputs(response);
    });
  };

  const uploadFile = async () => {
    await FilePicker.pickFiles({
      multiple: false,
      types: ["image/*"],
    }).then((e) => {
      setFile(new File([e.files[0].blob ?? ""], e.files[0].name));
    });
  };

  const uploadToAWS = async () => {
    const s3 = new ReactS3Client({
      ...s3Config,
      dirName: `pictureFolder`,
    });

    try {
      if (file) {
        await s3.uploadFile(file).then(async (response) => {
          await makePATCHRequest("/user?_id=" + inputs[4].value, {
            last_name: inputs[0].value,
            first_name: inputs[1].value,
            phone_number: inputs[2].value,
            picture_url: response.location,
          }).then(() => {
            window.location.reload();
          });
        });
      }
    } catch (exception) {}
  };

  const uploadWithoutPicture = async () => {
    await makeGETRequest("/me").then(async (response) => {
      await makePATCHRequest("/user?_id=" + inputs[4].value, {
        last_name: inputs[0].value,
        first_name: inputs[1].value,
        phone_number: inputs[2].value,
        picture_url: response.picture_url,
      }).then(() => {
        window.location.reload();
      });
    });
  };

  const handleClick = async () => {
    if (!file) {
      uploadWithoutPicture();
    } else {
      await uploadToAWS();
    }
  };

  useEffect(() => {
    fetchInfos();
    makeGETRequest("/me").then((res) => setUser(res));
    makeGETRequest("/notifications").then(
      (res) => (notifNumber.current = res.length)
    );
  }, []);

  useEffect(() => {
    if (inputs && inputs[4] && inputs[4].value) {
      makeGETRequest("/time?email=" + inputs[3].value).then((res) => {
        setTime([]);
        res.sort((a: any, b: any) => a.week_nb - b.week_nb);
        res = res.slice(-4);

        for (const week of res) {
          const { week_nb, time } = week;
          const monday = new Date(week_nb);
          const displayMonday =
            monday.getDate() +
            "/" +
            (monday.getMonth() + 1) +
            " au " +
            (monday.getDate() + 7) +
            "/" +
            (monday.getMonth() + 1);
          setTime((old) => [
            ...old,
            {
              name: displayMonday,
              time: parseFloat((time / 1000 / 3600).toFixed(2)),
            },
          ]);
        }
      });
    }
  }, [inputs]);

  return (
    <BasicPage
      pathReturn
      title={"Salut " + ((inputs && inputs[1] && inputs[1].value) ?? "") + ","}
      subtitle="Voici les informations sur ton compte"
    >
      <IonRow>
        <IonCol>
          {notifNumber.current > 0 && <Notifications />}
          <IonCard className="border-primary-contrast border-radius">
            <IonCardHeader>
              <h5
                style={{
                  color: "black",
                }}
              >
                Mon temps par semaine
              </h5>
            </IonCardHeader>
            <div
              style={{
                height: 300,
                margin: "10px 0 10px 0",
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart width={150} height={40} data={time}>
                  <Tooltip />
                  <Bar
                    name="Nombre d'heures"
                    dataKey="time"
                    fill="var(--ion-color-secondary-shade)"
                    radius={[30, 30, 0, 0]}
                  />

                  <XAxis
                    dataKey="name"
                    interval={0}
                    height={50}
                    angle={-15}
                    tick={{ dy: 20 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </IonCard>
          <IonCard className="border-primary-contrast border-radius ion-no-padding">
            <IonCardContent className=" ion-no-padding">
              <IonItem lines="none" shape="round">
                <IonThumbnail slot="start">
                  <img
                    style={{
                      borderRadius: "100%",
                    }}
                    alt="Image du Badge"
                    src={getRank(user?.level ?? 0, ranks).picture}
                  />
                </IonThumbnail>
                <IonText>
                  <p style={{ fontSize: 17 }}>
                    Je suis {getRank(user?.level ?? 0, ranks).name}
                  </p>
                </IonText>
                <IonText
                  slot="end"
                  className="border-secondary flex ion-align-items-center ion-justify-content-center"
                  style={{ borderRadius: 20, height: 56, padding: 5 }}
                >
                  niveau : {user?.level ?? 0}
                </IonText>
                {/* <IonIcon icon={closeCircleOutline} slot="end" /> */}
              </IonItem>
            </IonCardContent>
          </IonCard>
          <IonAccordionGroup>
            <IonAccordion
              className="border-primary-contrast border-radius bgColorDegrade"
              style={{ marginTop: 20 }}
            >
              <IonItem slot="header">Mes notes pour technologie</IonItem>
              <div
                slot="content"
                className="flex ion-justify-content-center"
                style={{
                  padding: 10,
                }}
              >
                {polesTechologie.map((pole, index) => {
                  return <StatModal key={index} pole={pole} genre={"techno"} />;
                })}
              </div>
            </IonAccordion>
            <IonAccordion
              className="border-primary-contrast border-radius bgColorDegrade"
              style={{
                marginTop: 20,
              }}
            >
              <IonItem slot="header">Mon résumé pour la pratique</IonItem>
              <div
                slot="content"
                className="flex ion-justify-content-center"
                style={{
                  padding: 10,
                }}
              >
                {polesTutorials.map((pole, index) => {
                  return (
                    <StatModal key={index} pole={pole} genre={"tutorial"} />
                  );
                })}
              </div>
            </IonAccordion>
            <IonAccordion
              className="border-primary-contrast border-radius bgColorDegrade"
              style={{
                marginTop: 20,
              }}
            >
              <IonItem slot="header">
                <IonLabel>Modifier mes informations</IonLabel>
              </IonItem>
              <div className="ion-padding" slot="content">
                <IonList
                  style={{
                    borderRadius: 20,
                    paddingLeft: 15,
                  }}
                >
                  {inputs.map((input, i) => {
                    return (
                      input.name !== "_id" && (
                        <IonItem key={i}>
                          <IonLabel position="stacked" color="black">
                            {input.name}
                          </IonLabel>
                          <IonInput
                            value={input.value}
                            type={input.type as TextFieldTypes}
                            onKeyUp={(e) => {
                              setInputs((old) => [
                                ...old.slice(0, i),
                                {
                                  ...old[i],
                                  change: true,
                                  value: (e.target as HTMLInputElement).value!,
                                },
                                ...old.slice(i + 1, old.length),
                              ]);
                            }}
                          ></IonInput>
                        </IonItem>
                      )
                    );
                  })}
                  <>
                    {isAndroid || isBrowser ? (
                      <div>
                        <label
                          htmlFor="upload-photo"
                          style={{
                            cursor: "pointer",
                            border:
                              "solid 2px var(--ion-color-primary-contrast)",
                            borderRadius: 20,
                            padding: 5,
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            boxShadow: "0px 5px 5px -3px rgba(0,0,0,0.75)",
                          }}
                        >
                          Choisir une image de profile
                        </label>
                        <input
                          type="file"
                          accept="image/png, image/gif, image/jpeg"
                          multiple={false}
                          name="photo"
                          id="upload-photo"
                          style={{
                            opacity: "0",
                            position: "absolute",
                            zIndex: "-1",
                          }}
                          onChange={(e) => {
                            const file = e.target.files?.item(0);
                            if (file !== null && file !== undefined) {
                              setFile(file);
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <IonButton
                        fill="solid"
                        shape="round"
                        expand="full"
                        style={{
                          "--border-color": "var(--ion-color-primary-contrast)",
                          "--border-style": "solid",
                          "--border-width": "2px",
                        }}
                        onClick={() => {
                          uploadFileToIos(setFile);
                        }}
                      >
                        Choisir une image de profile
                      </IonButton>
                    )}
                  </>
                  <IonGrid>
                    <IonRow className="ion-justify-content-center">
                      <IonCol
                        size="12"
                        sizeSm="12"
                        sizeLg="6"
                        className="ion-justify-content-center"
                      >
                        <IonText
                          className="btn fullWidth ion-justify-content-center flex"
                          onClick={handleClick}
                        >
                          Changer vos informations
                        </IonText>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonList>
              </div>
            </IonAccordion>
          </IonAccordionGroup>
        </IonCol>
      </IonRow>
      <IonRow className="ion-justify-content-center">
        <IonCol
          size="12"
          sizeSm="12"
          sizeLg="6"
          className="ion-justify-content-center"
        >
          <IonButton
            expand="full"
            color="secondary"
            shape="round"
            strong
            onClick={() => {
              localStorage.clear();
              window.location.reload();
            }}
          >
            Se déconnecter
          </IonButton>
        </IonCol>
      </IonRow>
      {inputs && inputs[3] && inputs[3].value && (
        <DeleteAccount userEmail={inputs[3].value.toString()} />
      )}
    </BasicPage>
  );
};

export default Account;
